import { type FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors
} from '@dnd-kit/core'
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers'
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { t } from 'i18next'
import { useInfiniteQuery } from 'react-query'
import { useNavigate } from 'react-router-dom'
import BeatLoader from 'react-spinners/BeatLoader'
import styled from 'styled-components'

import { Button } from 'components/Button/Button'
import { addToast } from 'components/Toast/Toast'
import { RadioButton } from 'components-ref/atoms'
import { useModalContext } from 'context/modalContext'
import SubscriptionsList from 'features/SubscriptionsList/SubscriptionsList'
import {
  getSubscriptionLists,
  updateSubscriptionList
} from 'services/endpoints/subscriptionList'
import { useAuthStore } from 'store'
import { useProfileCacheStore } from 'store/profileCacheStore'
import { useSubscriptionListsCacheStore } from 'store/subscriptionListsCacheStore'
import { type ISubscriptionList } from 'types/interfaces/ITypes'

interface ManageFeedProps {
  onApply?: (_id: string) => void
  chosenListId?: string
}

export const ManageFeed: FC<ManageFeedProps> = () => {
  const [selectedLists, setSelectedLists] = useState<string[]>([])
  const initialSelectedRef = useRef<string[]>([])
  const initialOrderRef = useRef<string[]>([])

  const modalData = useModalContext()
  const navigate = useNavigate()

  const userData = useAuthStore(state => state.userProfile)
  const didInitSelectedRef = useRef(false)

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 6
      }
    })
  )

  const cacheEntry = useSubscriptionListsCacheStore(state =>
    state.entries['subscriptionLists:recently_added:asc']
  )
  const hasCachedLists = Array.isArray(cacheEntry?.lists)

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isLoading: queryLoading,
    isFetchingNextPage
  } = useInfiniteQuery(
    ['allSubscriptionLists'],
    async ({ pageParam = 1 }) =>
      await getSubscriptionLists('recently_added', 'asc', pageParam),
    {
      getNextPageParam: lastPage => lastPage?.nextCursor,
      enabled: !hasCachedLists,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      staleTime: 600_000
    }
  )

  const isLoading = !hasCachedLists && queryLoading

  useEffect(() => {
    if (hasCachedLists || !data?.pages?.length || hasNextPage) return

    const lists = data.pages.flatMap(page => page?.page?.data?.data ?? [])

    useSubscriptionListsCacheStore.getState().setLists(
      'recently_added',
      'asc',
      lists.map(item => ({ data: item }))
    )
  }, [data, hasNextPage, hasCachedLists])

  const getAllListId = (lists: any[]) => {
    const byDefault = lists.find((l: any) => Number(l.default) === 1)
    if (byDefault) return byDefault.id

    const byName = lists.find((l: any) => String(l.name).toLowerCase() === 'all')
    return byName?.id
  }

  const normalizeSelectionWithAll = (
    lists: any[],
    selected: string[]
  ) => {
    const allId = getAllListId(lists)
    if (!allId) return selected

    const nonAllIds = lists
      .map((l: any) => l.id)
      .filter((id: string) => id !== allId)

    const allNonAllSelected =
      nonAllIds.length > 0 && nonAllIds.every(id => selected.includes(id))

    if (allNonAllSelected && !selected.includes(allId)) {
      return [...selected, allId]
    }

    if (!allNonAllSelected && selected.includes(allId)) {
      return selected.filter(id => id !== allId)
    }

    return selected
  }

  const authLists = useMemo(() => {
    const lists = (userData?.selected_lists ?? []) as any[]

    return lists.map(l => ({
      id: l.id,
      name: l.name,
      selected: l.selected,
      order: l.order,
      avatars: [],
      count: 0,
      default: l.default
    }))
  }, [userData?.selected_lists])

  const queryLists = useMemo(() => {
    if (hasCachedLists) {
      return cacheEntry.lists.map(item => item.data)
    }

    return data?.pages.flatMap(page => page?.page?.data?.data ?? []) ?? []
  }, [cacheEntry, hasCachedLists, data])

  const listData = useMemo(() => {
    const queryById = new Map<string, any>(
      queryLists.map(l => [l.id, l])
    )

    const lists = authLists.map(a => {
      const q = queryById.get(a.id)

      if (!q) return a

      return {
        ...q,
        selected: a.selected,
        order: a.order ?? q.order
      }
    })

    return [...lists].sort(
      (a, b) => Number(a.order ?? 999) - Number(b.order ?? 999)
    )
  }, [authLists, queryLists])
const [orderedLists, setOrderedLists] = useState<any[]>(() => listData)
  const noLists = !isLoading && listData.length === 0

  useEffect(() => {
    setOrderedLists(listData)
  }, [listData])

  useEffect(() => {
    if (didInitSelectedRef.current) return
    if (!authLists.length) return

    const selected = authLists.filter(l => !!l.selected).map(l => l.id)
    const initial = selected.length ? selected : [authLists[0].id]

    setSelectedLists(initial)

    initialSelectedRef.current = [...initial]
    initialOrderRef.current = [...listData.map(l => l.id)]

    didInitSelectedRef.current = true
  }, [authLists, listData])

  const hasChanges = useMemo(() => {
    if (!didInitSelectedRef.current) return false

  const currentSelected = [...selectedLists].sort()
  const originalSelected = [...initialSelectedRef.current].sort()

    const selectedChanged =
      JSON.stringify(currentSelected) !== JSON.stringify(originalSelected)

    const currentOrder = orderedLists.map(l => l.id)
    const originalOrder = initialOrderRef.current

    const orderChanged =
      JSON.stringify(currentOrder) !== JSON.stringify(originalOrder)

    return selectedChanged || orderChanged
  }, [selectedLists, orderedLists])


  const handleDragEnd = ({ active, over }: any) => {
    if (!over || active.id === over.id) return

    setOrderedLists(items => {
      const oldIndex = items.findIndex(item => item.id === active.id)
      const newIndex = items.findIndex(item => item.id === over.id)

      if (oldIndex === -1 || newIndex === -1) return items

      return arrayMove(items, oldIndex, newIndex).map((item, index) => ({
        ...item,
        order: index + 1
      }))
    })
  }

  const observer = useRef<IntersectionObserver>()
  const lastMessageRef = useCallback(
    (node: any) => {
      if (isFetchingNextPage || isLoading) return
      if (observer.current) observer.current.disconnect()

      observer.current = new IntersectionObserver(
        entries => {
          if (entries[0].isIntersecting && hasNextPage) {
            fetchNextPage()
          }
        },
        { rootMargin: '150px' }
      )

      if (node) observer.current.observe(node)
    },
    [isFetchingNextPage, isLoading, hasNextPage, fetchNextPage]
  )

  const toggleList = (id: string) => {
    const lists = orderedLists ?? []
    if (!lists.length) return

    const allId = getAllListId(lists)
    const nonAllIds = lists
      .map((l: any) => l.id)
      .filter((x: string) => x !== allId)

    if (allId && id === allId) {
      const isAllSelected = selectedLists.includes(allId)

      if (isAllSelected) {
        if (nonAllIds.length > 0) setSelectedLists([nonAllIds[0]])
        else setSelectedLists([allId])
      } else {
        setSelectedLists([allId, ...nonAllIds])
      }

      return
    }

    const isSelected = selectedLists.includes(id)

    let next = isSelected
      ? selectedLists.filter(x => x !== id)
      : [...selectedLists, id]

    if (next.length === 0) {
      if (allId) next = [allId]
      else next = [lists[0].id]
    }

    next = normalizeSelectionWithAll(lists, next)
    setSelectedLists(next)
  }

  const onApply = async () => {
    if (!orderedLists.length) {
      modalData.clearModal()
      return
    }

    const allId = getAllListId(orderedLists)

    const idsToSend = allId
      ? selectedLists.filter(id => id !== allId)
      : selectedLists

    const orders = orderedLists.map((list, index) => ({
      id: list.id,
      order: index + 1
    }))

    try {
      await updateSubscriptionList(orderedLists[0].id, {
        selected: idsToSend,
        orders
      })

      const me = useAuthStore.getState().userProfile

      if (me) {
        const updatedSelectedLists = orderedLists.map((list: any, index: number) => {
          const isAll = allId && list.id === allId

          return {
            id: list.id,
            name: list.name,
            order: index + 1,
            selected: isAll
              ? selectedLists.includes(allId) ? 1 : 0
              : idsToSend.includes(list.id) ? 1 : 0
          }
        })

        useAuthStore.getState().setUserProfile({
          ...me,
          selected_lists: updatedSelectedLists
        } as any)

        useProfileCacheStore.getState().setProfile(
          me.username,
          { ...me, selected_lists: updatedSelectedLists } as any,
          me.id
        )
      }

      modalData.clearModal()
    } catch (error) {
      addToast('error', 'An error occured, please try again')
    }
  }

  return (
    <div>
      <ul className="groupsDd__body__list">
        <div className="groupsDd__body__list--primary">
          {isLoading ? (
            <LoaderWrapper>
              <BeatLoader size={8} />
            </LoaderWrapper>
          ) : noLists ? (
            <EmptyText>No Lists Exist</EmptyText>
          ) : (
            <>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                modifiers={[restrictToVerticalAxis, restrictToParentElement]}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={orderedLists.map(list => list.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {orderedLists.map((list: ISubscriptionList) => (
                    <SortableListItem
                      key={list.id}
                      list={list}
                      selected={selectedLists.includes(list.id)}
                      onToggle={() => {
                        toggleList(list.id)
                      }}
                    />
                  ))}
                </SortableContext>
              </DndContext>

              {!hasCachedLists && hasNextPage && (
                <div className="loader" ref={lastMessageRef}></div>
              )}
            </>
          )}
        </div>
      </ul>

      <ButtonsWrapper>
        <Button
          text={t('cancel')}
          color="grey"
          font="mont-14-normal"
          width="14"
          height="3"
          clickFn={() => {
            modalData.clearModal()
          }}
        />

        <Button
          text={noLists ? 'Goto Lists' : t('apply')}
          color="black"
          width="14"
          height="3"
          font="mont-14-normal"
          disabled={!noLists && !hasChanges}
          clickFn={() => {
            if (noLists) {
              modalData.clearModal()
              navigate('/subscriptions/lists')
              return
            }

            onApply()
          }}
        />
      </ButtonsWrapper>
    </div>
  )
}

const SortableListItem: FC<{
  list: ISubscriptionList
  selected: boolean
  onToggle: () => void
}> = ({ list, selected, onToggle }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({
    id: list.id
  })

  return (
    <ListItem
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 50 : undefined,
        opacity: isDragging ? 0.85 : 1
      }}
      onClick={onToggle}
    >
      <DragHandle
        type="button"
        aria-label="Drag to reorder"
        onClick={e => {
          e.stopPropagation()
        }}
        {...attributes}
        {...listeners}
      >
        ☰
      </DragHandle>

      <FanListWrapper>
        <SubscriptionsList
          title={list.name}
          hideOptions
          disableNavigate
          fans={{
            avatars: (list as any).avatars ?? [],
            count: (list as any).count ?? 0,
            data: list
          }}
        />
      </FanListWrapper>

      <RadioButtonWrapper>
        <RadioButton active={selected} />
      </RadioButtonWrapper>
    </ListItem>
  )
}

const RadioButtonWrapper = styled.div`
  position: absolute;
  top: 0;
  right: 1.2rem;
  bottom: 0;

  display: flex;
  align-items: center;
  justify-content: center;
`

const ListItem = styled.div`
  position: relative;
  display: flex;
  align-items: stretch;
  margin-bottom: 10px;
`

const DragHandle = styled.button`
  flex-shrink: 0;
  width: 3.5rem;
  border: 0;
  background: transparent;
  cursor: grab;
  touch-action: none;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #9b9b9b;
  font-size: 2rem;
  line-height: 1;

  &:active {
    cursor: grabbing;
  }
`

const FanListWrapper = styled.div`
  width: 100%;

  > .fansList,
  > div {
    margin-bottom: 0;
    padding-right: 4rem;
  }
`

const LoaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem 0;
  padding-bottom: 3rem;
`

const EmptyText = styled.div`
  text-align: center;
  padding: 2rem 0;
  font-size: 1.4rem;
  font-weight: 500;
  color: #a1a1a1;
  padding-bottom: 3rem;
`

const ButtonsWrapper = styled.div`
  display: flex;
  justify-content: space-around;
  align-items: center;
  width: 100%;
  padding-top: 2rem;
  padding-bottom: 1rem;
`